import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { MainState } from "@app/store/state";
import { setTimeagoIntl } from "@app/translate-loader";
import { UserProfileInterface } from "@core/interfaces/user-profile.interface";
import { UserSubscriptionInterface } from "@core/interfaces/user-subscription.interface";
import { UserInterface } from "@core/interfaces/user.interface";
import { CommonApiService } from "@core/services/api/classic/common/common-api.service";
import { AuthService } from "@core/services/auth.service";
import { LoadingService } from "@core/services/loading.service";
import { PopNotificationsService } from "@core/services/pop-notifications.service";
import { WindowRefService } from "@core/services/window-ref.service";
import { Login2FARequired } from "@features/account/store/auth-2fa.actions";
import {
  ChangeUserProfileGalleryHeaderImage,
  DeleteAvatar,
  LoadUser,
  LoadUserProfile,
  Login,
  RemoveShadowBanUserProfile,
  ShadowBanUserProfile,
  UpdateUserProfile,
  UploadAvatar,
  AuthActionTypes,
  ChangeUserProfileGalleryHeaderImageFailure,
  ChangeUserProfileGalleryHeaderImageSuccess,
  DeleteAvatarFailure,
  DeleteAvatarSuccess,
  InitializeAuthSuccess,
  LoadUserFailure,
  LoadUserProfileFailure,
  LoadUserProfileSuccess,
  LoadUserSuccess,
  LoginFailure,
  LoginSuccess,
  LogoutSuccess,
  RemoveShadowBanUserProfileFailure,
  RemoveShadowBanUserProfileSuccess,
  ShadowBanUserProfileFailure,
  ShadowBanUserProfileSuccess,
  UpdateUserProfileSuccess,
  UploadAvatarFailure,
  UploadAvatarSuccess,
  CompleteTutorial,
  CompleteTutorialSuccess,
  CompleteTutorialFailure,
  ResetTutorial,
  ResetTutorialSuccess,
  ResetTutorialFailure
} from "@features/account/store/auth.actions";
import { LoginSuccessInterface } from "@features/account/store/auth.actions.interfaces";
import { selectUser, selectUserByUsername, selectUserProfile } from "@features/account/store/auth.selectors";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { Constants } from "@shared/constants";
import { CookieService } from "ngx-cookie";
import { TimeagoIntl } from "ngx-timeago";
import { EMPTY, Observable, of, forkJoin } from "rxjs";
import { catchError, map, mergeMap, switchMap, tap } from "rxjs/operators";

@Injectable()
export class AuthEffects {
  LoginSuccess: Observable<LoginSuccess> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActionTypes.LOGIN_SUCCESS),
        tap((action: LoginSuccess) => {
          this.loadingService.setLoading(false);
          void this.router.navigate(["account", "logged-in"], {
            queryParams: { redirectUrl: action.payload.redirectUrl }
          });
        })
      ),
    { dispatch: false }
  );

  LoginFailure: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActionTypes.LOGIN_FAILURE),
        tap(() => {
          this.loadingService.setLoading(false);
        })
      ),
    { dispatch: false }
  );

  Logout: Observable<LogoutSuccess> = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActionTypes.LOGOUT),
      tap(() => this.loadingService.setLoading(true)),
      switchMap(() => this.authService.logout()),
      map(() => new LogoutSuccess())
    )
  );

  LogoutSuccess: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActionTypes.LOGOUT_SUCCESS),
        tap(() => {
          this.loadingService.setLoading(false);
          // Success message is shown on the logged-out page
        })
      ),
    { dispatch: false }
  );

  UpdateUserProfile: Observable<UpdateUserProfileSuccess> = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActionTypes.UPDATE_USER_PROFILE),
      map((action: UpdateUserProfile) => action.payload),
      switchMap(payload =>
        this.commonApiService
          .updateUserProfile(payload.id, payload)
          .pipe(map(result => new UpdateUserProfileSuccess(result)))
      )
    )
  );

  LoadUser: Observable<LoadUserSuccess | LoadUserFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActionTypes.LOAD_USER),
      map((action: LoadUser) => action.payload),
      mergeMap(payload => {
        let selector;
        let selectorArgument;

        if (payload.id) {
          selector = selectUser;
          selectorArgument = payload.id;
        } else if (payload.username) {
          selector = selectUserByUsername;
          selectorArgument = payload.username;
        }

        if (!selector || !selectorArgument) {
          return EMPTY;
        }

        return this.store$.select(selector, selectorArgument).pipe(
          switchMap(userFromStore =>
            userFromStore !== null
              ? of(userFromStore).pipe(map(() => new LoadUserSuccess({ user: userFromStore })))
              : this.commonApiService.getUser(payload.id, payload.username).pipe(
                  map(user => (!!user ? new LoadUserSuccess({ user }) : new LoadUserFailure(payload))),
                  catchError(error => of(new LoadUserFailure(payload)))
                )
          )
        );
      })
    )
  );

  LoadUserProfile: Observable<LoadUserProfileSuccess> = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActionTypes.LOAD_USER_PROFILE),
      map((action: LoadUserProfile) => action.payload),
      mergeMap(payload =>
        this.store$.select(selectUserProfile, payload.id).pipe(
          switchMap(userProfileFromStore =>
            userProfileFromStore !== null
              ? of(userProfileFromStore).pipe(
                  map(() => new LoadUserProfileSuccess({ userProfile: userProfileFromStore }))
                )
              : this.commonApiService.getUserProfile(payload.id).pipe(
                  map(
                    userProfile => new LoadUserProfileSuccess({ userProfile }),
                    catchError(error => of(new LoadUserProfileFailure({ id: payload.id, error })))
                  )
                )
          )
        )
      )
    )
  );

  ChangeUserProfileGalleryHeaderImage: Observable<
    ChangeUserProfileGalleryHeaderImageSuccess | ChangeUserProfileGalleryHeaderImageFailure
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActionTypes.CHANGE_USER_PROFILE_GALLERY_HEADER_IMAGE),
      map((action: ChangeUserProfileGalleryHeaderImage) => action.payload),
      switchMap(payload =>
        this.commonApiService.changeUserProfileGalleryHeaderImage(payload.id, payload.imageId).pipe(
          tap(() => this.loadingService.setLoading(true)),
          map(userProfile => new ChangeUserProfileGalleryHeaderImageSuccess({ userProfile, imageId: payload.imageId })),
          catchError(error =>
            of(
              new ChangeUserProfileGalleryHeaderImageFailure({
                id: payload.id,
                imageId: payload.imageId,
                error
              })
            )
          )
        )
      )
    )
  );

  ChangeUserProfileGalleryHeaderImageSuccess: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActionTypes.CHANGE_USER_PROFILE_GALLERY_HEADER_IMAGE_SUCCESS),
        tap(() => {
          this.loadingService.setLoading(false);
        })
      ),
    { dispatch: false }
  );

  ChangeUserProfileGalleryHeaderImageFailure: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActionTypes.CHANGE_USER_PROFILE_GALLERY_HEADER_IMAGE_FAILURE),
        tap(() => {
          this.loadingService.setLoading(false);
        })
      ),
    { dispatch: false }
  );

  ShadowBanUserProfile: Observable<ShadowBanUserProfileSuccess | ShadowBanUserProfileFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActionTypes.SHADOW_BAN_USER_PROFILE),
      map((action: ShadowBanUserProfile) => action.payload),
      switchMap(payload =>
        this.commonApiService.shadowBanUserProfile(payload.id).pipe(
          map(
            response =>
              new ShadowBanUserProfileSuccess({
                id: payload.id,
                userProfileId: payload.userProfileId,
                message: response.message
              })
          ),
          catchError(error =>
            of(
              new ShadowBanUserProfileFailure({
                id: payload.id,
                userProfileId: payload.userProfileId,
                error
              })
            )
          )
        )
      )
    )
  );

  ShadowBanUserProfileSuccess: Observable<string> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActionTypes.SHADOW_BAN_USER_PROFILE_SUCCESS),
        map((action: ShadowBanUserProfileSuccess) => action.payload.message),
        tap(message => {
          this.popNotificationsService.success(message);
          this.loadingService.setLoading(false);
        })
      ),
    { dispatch: false }
  );

  ShadowBanUserProfileFailure: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActionTypes.SHADOW_BAN_USER_PROFILE_FAILURE),
        tap(() => {
          this.popNotificationsService.error("Failed to shadow ban user.");
          this.loadingService.setLoading(false);
        })
      ),
    { dispatch: false }
  );

  RemoveShadowBanUserProfile: Observable<RemoveShadowBanUserProfileSuccess | RemoveShadowBanUserProfileFailure> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(AuthActionTypes.REMOVE_SHADOW_BAN_USER_PROFILE),
        map((action: RemoveShadowBanUserProfile) => action.payload),
        switchMap(payload =>
          this.commonApiService.removeShadowBanUserProfile(payload.id).pipe(
            map(
              response =>
                new RemoveShadowBanUserProfileSuccess({
                  id: payload.id,
                  userProfileId: payload.userProfileId,
                  message: response.message
                })
            ),
            catchError(error =>
              of(
                new RemoveShadowBanUserProfileFailure({
                  id: payload.id,
                  userProfileId: payload.userProfileId,
                  error
                })
              )
            )
          )
        )
      )
    );

  RemoveShadowBanUserProfileSuccess: Observable<string> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActionTypes.REMOVE_SHADOW_BAN_USER_PROFILE_SUCCESS),
        map((action: RemoveShadowBanUserProfileSuccess) => action.payload.message),
        tap(message => {
          this.popNotificationsService.success(message);
          this.loadingService.setLoading(false);
        })
      ),
    { dispatch: false }
  );

  RemoveShadowBanUserProfileFailure: Observable<void> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActionTypes.REMOVE_SHADOW_BAN_USER_PROFILE_FAILURE),
        tap(() => {
          this.popNotificationsService.error("Failed to remove shadow ban from user.");
          this.loadingService.setLoading(false);
        })
      ),
    { dispatch: false }
  );

  private _getData$: Observable<{
    user: UserInterface;
    userProfile: UserProfileInterface;
    userSubscriptions: UserSubscriptionInterface[];
  }> = forkJoin({
    user: this.commonApiService.getCurrentUser(),
    userProfile: this.commonApiService.getCurrentUserProfile(),
    userSubscriptions: this.commonApiService.getCurrentUserSubscriptions()
  }).pipe(
    map(data => {
      if (!data.user || !data.userProfile) {
        return null;
      }
      return data;
    })
  );

  Login: Observable<LoginSuccess | LoginFailure | Login2FARequired> = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActionTypes.LOGIN),
      map((action: Login) => action),
      tap(action => {
        this.loadingService.setLoading(true);
        return action;
      }),
      switchMap(action =>
        this.authService.login(action.payload.handle, action.payload.password, action.payload.redirectUrl).pipe(
          switchMap(response => {
            if (response.requires_2fa) {
              // 2FA is required
              return of(
                new Login2FARequired({
                  sessionToken: response.session_token,
                  twoFactorMethod: response.two_fa_method,
                  emailHint: response.email_hint
                })
              );
            } else if (response.token) {
              // Direct login success (no 2FA)
              const url = this.windowRefService.getCurrentUrl().toString();
              const options: any = {
                path: "/",
                expires: new Date(new Date().getTime() + 180 * 24 * 60 * 60 * 1000)
              };

              // Add domain based on current URL
              if (url?.includes("astrobin.com")) {
                options.domain = ".astrobin.com";
              } else if (url?.includes("localhost")) {
                options.domain = "localhost";
              }

              this.cookieService.put(AuthService.CLASSIC_AUTH_TOKEN_COOKIE, response.token, options);

              return this._getData$.pipe(
                map(data => ({
                  user: data.user,
                  userProfile: data.userProfile,
                  userSubscriptions: data.userSubscriptions,
                  redirectUrl: action.payload.redirectUrl
                })),
                switchMap(data => {
                  if (data && data.userProfile && data.userProfile.language) {
                    return this._setLanguage(data.userProfile.language).pipe(map(() => data));
                  } else {
                    return of(data);
                  }
                }),
                map(payload => new LoginSuccess(payload))
              );
            } else {
              // Unexpected response
              return of(new LoginFailure({ error: "Unexpected authentication response" }));
            }
          }),
          catchError(error => of(new LoginFailure({ error })))
        )
      )
    )
  );

  Initialize: Observable<InitializeAuthSuccess> = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActionTypes.INITIALIZE),
      switchMap(() =>
        new Observable<LoginSuccessInterface>(observer => {
          const token = this.cookieService.get(AuthService.CLASSIC_AUTH_TOKEN_COOKIE);
          if (!!token) {
            this._getData$.subscribe(data => {
              if (!data || !data.user || !data.userProfile || !data.userSubscriptions) {
                observer.next({ user: null, userProfile: null, userSubscriptions: [] });
                observer.complete();
                return;
              }

              this._setLanguage(data.userProfile.language).subscribe(() => {
                const successPayload: LoginSuccessInterface = {
                  user: data.user,
                  userProfile: data.userProfile,
                  userSubscriptions: data.userSubscriptions
                };

                observer.next(successPayload);
                observer.complete();
              });
            });
          } else {
            observer.next({ user: null, userProfile: null, userSubscriptions: [] });
            observer.complete();
          }
        }).pipe(map(payload => new InitializeAuthSuccess(payload)))
      )
    )
  );

  constructor(
    public readonly store$: Store<MainState>,
    public readonly actions$: Actions,
    public readonly authService: AuthService,
    public readonly router: Router,
    public readonly cookieService: CookieService,
    public readonly loadingService: LoadingService,
    public readonly commonApiService: CommonApiService,
    public readonly translateService: TranslateService,
    public readonly timeagoIntl: TimeagoIntl,
    public readonly popNotificationsService: PopNotificationsService,
    public readonly windowRefService: WindowRefService
  ) {}

  private _setLanguage(language: string): Observable<any> {
    this.translateService.setDefaultLang(language);
    return this.translateService.use(language).pipe(
      map(() => {
        setTimeagoIntl(this.timeagoIntl, language);
      })
    );
  }

  UploadAvatar: Observable<UploadAvatarSuccess | UploadAvatarFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActionTypes.UPLOAD_AVATAR),
      tap(() => {
        this.loadingService.setLoading(true);
      }),
      switchMap((action: UploadAvatar) =>
        this.commonApiService.uploadAvatar(action.payload.avatarFile).pipe(
          map(response => {
            if (response.success) {
              return new UploadAvatarSuccess({
                avatarId: response.avatar_id,
                avatarUrl: response.avatar_url
              });
            } else {
              // The server-errors.interceptor will handle displaying the error
              // Just return the failure action
              return new UploadAvatarFailure({ error: response.errors || response });
            }
          }),
          catchError(error => {
            // For HTTP errors, the server-errors.interceptor will handle the display
            // For non-HTTP errors (like network errors), show a generic message
            if (!error.status && !error.url) {
              this.popNotificationsService.error(
                this.translateService.instant("Error uploading avatar. Please check your connection and try again.")
              );
            }

            this.loadingService.setLoading(false);
            return of(new UploadAvatarFailure({ error }));
          }),
          tap(() => {
            this.loadingService.setLoading(false);
          })
        )
      )
    )
  );

  DeleteAvatar: Observable<DeleteAvatarSuccess | DeleteAvatarFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActionTypes.DELETE_AVATAR),
      tap(() => {
        this.loadingService.setLoading(true);
      }),
      switchMap((action: DeleteAvatar) =>
        this.commonApiService.deleteAvatar(action.payload.avatarId).pipe(
          map(response => {
            if (response.success) {
              return new DeleteAvatarSuccess({
                avatarId: action.payload.avatarId,
                avatarUrl: response.avatar_url || Constants.DEFAULT_AVATAR
              });
            } else {
              // Handle error message properly for translation
              const errorMessage = response.message
                ? response.message
                : this.translateService.instant("Error deleting avatar");
              this.popNotificationsService.error(this.translateService.instant(errorMessage));
              return new DeleteAvatarFailure({ avatarId: action.payload.avatarId, error: response.message });
            }
          }),
          catchError(error => {
            this.popNotificationsService.error(this.translateService.instant("Error deleting avatar"));
            this.loadingService.setLoading(false);
            return of(new DeleteAvatarFailure({ avatarId: action.payload.avatarId, error }));
          }),
          tap(() => {
            this.loadingService.setLoading(false);
          })
        )
      )
    )
  );

  CompleteTutorial: Observable<CompleteTutorialSuccess | CompleteTutorialFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActionTypes.COMPLETE_TUTORIAL),
      map((action: CompleteTutorial) => action.payload),
      switchMap(payload =>
        this.commonApiService.completeTutorial(payload.tutorialId).pipe(
          map(completedTutorials => new CompleteTutorialSuccess({ completedTutorials })),
          catchError(error => of(new CompleteTutorialFailure({ tutorialId: payload.tutorialId, error })))
        )
      )
    )
  );

  ResetTutorial: Observable<ResetTutorialSuccess | ResetTutorialFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActionTypes.RESET_TUTORIAL),
      map((action: ResetTutorial) => action.payload),
      switchMap(payload =>
        this.commonApiService.resetTutorial(payload.tutorialId).pipe(
          map(() => new ResetTutorialSuccess({ tutorialId: payload.tutorialId })),
          catchError(error => of(new ResetTutorialFailure({ tutorialId: payload.tutorialId, error })))
        )
      )
    )
  );
}
